import React from 'react';
import { Badge } from 'reactstrap';
import { formatAlphaPrice } from '../../../../../../utils/deliveryDetailUtils';

const hasFilledValue = value =>
  value != null && String(value).trim() !== '' && String(value).trim() !== '-';

const pickFirstFilled = (...values) => {
  for (const value of values) {
    if (hasFilledValue(value)) return value;
  }
  return null;
};

const normalizeMatchPart = value => {
  if (!hasFilledValue(value)) return '';
  return String(value).trim().toLowerCase().replace(/\s+/g, ' ');
};

const normalizeQtyPart = value => {
  if (!hasFilledValue(value)) return '';
  const numeric = Number(String(value).replace(/[^\d.-]/g, ''));
  if (Number.isFinite(numeric)) return String(numeric);
  return normalizeMatchPart(value);
};

export const MATCHING_HPS_LABEL = {
  unavailable: 'N/A',
  lower: 'Lebih Rendah',
  equal: 'Sama',
  higher: 'Lebih Tinggi',
};

const HPS_EQUAL_TOLERANCE = 1e-9;

export const parseAlphaNumber = value => {
  if (!hasFilledValue(value)) return null;
  const numeric = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
};

const readHpsAlphaValues = source => ({
  alpha: pickFirstFilled(
    source?.hpsAlpha,
    source?.alphaHps,
    source?.HpsAlpha,
    source?.AlphaHps
  ),
  dyed: pickFirstFilled(
    source?.hpsAlphaDyed,
    source?.hpsDyed,
    source?.alphaHpsDyed,
    source?.HpsAlphaDyed,
    source?.alphaDyedHps,
    source?.AlphaDyedHps
  ),
  undyed: pickFirstFilled(
    source?.hpsAlphaUndyed,
    source?.hpsUndyed,
    source?.alphaHpsUndyed,
    source?.HpsAlphaUndyed,
    source?.alphaUndyedHps,
    source?.AlphaUndyedHps
  ),
});

export const hasHpsAlphaValues = hps =>
  parseAlphaNumber(hps?.alpha) != null ||
  parseAlphaNumber(hps?.dyed) != null ||
  parseAlphaNumber(hps?.undyed) != null;

const buildPortQtyPart = (port, qty) => {
  const portPart = normalizeMatchPart(port);
  if (!portPart) return '';
  const qtyPart = normalizeQtyPart(qty);
  return qtyPart ? `${portPart}:${qtyPart}` : portPart;
};

export const buildHpsMatchKey = (fields = {}) => {
  const basis = normalizeQtyPart(
    fields.deliveryBasis ?? fields.parcelSize ?? fields.dischargeQty1
  );
  const port = buildPortQtyPart(
    fields.fobOriginLocationName ||
      fields.loadingPort ||
      fields.port1 ||
      fields.Port1,
    fields.parcelSize ?? fields.dischargeQty1 ?? fields.deliveryBasis
  );
  const port2 = buildPortQtyPart(
    fields.port2 ?? fields.Port2,
    fields.parcelSize2 ?? fields.dischargeQty2
  );
  const port3 = buildPortQtyPart(
    fields.port3 ?? fields.Port3,
    fields.parcelSize3 ?? fields.dischargeQty3
  );
  return [
    basis,
    normalizeMatchPart(fields.incoterm),
    normalizeMatchPart(
      fields.route ?? fields.incotermRoute ?? fields.incotermPortType
    ),
    [port, port2, port3].filter(Boolean).join('|'),
  ].join('::');
};

const pickFilledMatchField = (...values) => {
  for (const value of values) {
    if (hasFilledValue(value)) return value;
  }
  return null;
};

export const resolveOfferHpsMatchFields = (
  detailRow = {},
  { isCounter = false } = {}
) => {
  if (isCounter) {
    const incoterm = pickFilledMatchField(
      detailRow.counterIncoterm,
      detailRow.incoterm
    );
    const isFob =
      String(incoterm || '')
        .trim()
        .toUpperCase() === 'FOB';
    const counterPort1 = isFob
      ? pickFilledMatchField(
          detailRow.counterLoadingPort,
          detailRow.port1,
          detailRow.loadingPort,
          detailRow.fobOriginLocationName
        )
      : pickFilledMatchField(
          detailRow.counterLoadingPort,
          detailRow.port1,
          detailRow.loadingPort
        );
    return {
      deliveryBasis: pickFilledMatchField(
        detailRow.counterDeliveryBasis,
        detailRow.counterParcelSize,
        detailRow.deliveryBasis,
        detailRow.parcelSize
      ),
      parcelSize: pickFilledMatchField(
        detailRow.counterParcelSize,
        detailRow.counterDeliveryBasis,
        detailRow.parcelSize,
        detailRow.deliveryBasis
      ),
      parcelSize2: pickFilledMatchField(
        detailRow.counterParcelSize2,
        detailRow.parcelSize2,
        detailRow.dischargeQty2
      ),
      parcelSize3: pickFilledMatchField(
        detailRow.counterParcelSize3,
        detailRow.parcelSize3,
        detailRow.dischargeQty3
      ),
      dischargeQty1: pickFilledMatchField(
        detailRow.counterParcelSize,
        detailRow.parcelSize,
        detailRow.dischargeQty1
      ),
      dischargeQty2: pickFilledMatchField(
        detailRow.counterParcelSize2,
        detailRow.parcelSize2,
        detailRow.dischargeQty2
      ),
      dischargeQty3: pickFilledMatchField(
        detailRow.counterParcelSize3,
        detailRow.parcelSize3,
        detailRow.dischargeQty3
      ),
      incoterm,
      route: pickFilledMatchField(
        detailRow.counterRoute,
        detailRow.route,
        detailRow.incotermRoute,
        detailRow.incotermPortType
      ),
      port1: counterPort1,
      port2: pickFilledMatchField(detailRow.counterPort2, detailRow.port2),
      port3: pickFilledMatchField(detailRow.counterPort3, detailRow.port3),
      loadingPort: isFob ? counterPort1 : null,
      fobOriginLocationName: isFob ? counterPort1 : null,
      incotermLocationId: isFob ? detailRow.incotermLocationId : null,
    };
  }

  return {
    deliveryBasis: pickFilledMatchField(
      detailRow.deliveryBasis,
      detailRow.parcelSize,
      detailRow.dischargeQty1
    ),
    parcelSize: pickFilledMatchField(
      detailRow.parcelSize,
      detailRow.dischargeQty1,
      detailRow.deliveryBasis
    ),
    parcelSize2: pickFilledMatchField(
      detailRow.parcelSize2,
      detailRow.dischargeQty2
    ),
    parcelSize3: pickFilledMatchField(
      detailRow.parcelSize3,
      detailRow.dischargeQty3
    ),
    dischargeQty1: pickFilledMatchField(
      detailRow.dischargeQty1,
      detailRow.parcelSize
    ),
    dischargeQty2: pickFilledMatchField(
      detailRow.dischargeQty2,
      detailRow.parcelSize2
    ),
    dischargeQty3: pickFilledMatchField(
      detailRow.dischargeQty3,
      detailRow.parcelSize3
    ),
    incoterm: detailRow.incoterm,
    route: pickFilledMatchField(
      detailRow.route,
      detailRow.incotermRoute,
      detailRow.incotermPortType
    ),
    port1: pickFilledMatchField(
      detailRow.port1,
      detailRow.loadingPort,
      detailRow.fobOriginLocationName
    ),
    port2: detailRow.port2,
    port3: detailRow.port3,
    loadingPort: pickFilledMatchField(
      detailRow.loadingPort,
      detailRow.fobOriginLocationName
    ),
    fobOriginLocationName: detailRow.fobOriginLocationName,
    incotermLocationId: detailRow.incotermLocationId,
  };
};

const readHpsFromFobLocations = row => {
  const locations = Array.isArray(row?.incotermFOBLocations)
    ? row.incotermFOBLocations
    : [];
  if (!locations.length) return null;

  const locId = String(row?.incotermLocationId ?? '');
  const locName = normalizeMatchPart(
    row?.fobOriginLocationName || row?.loadingPort || row?.port1
  );
  const loc =
    (locId
      ? locations.find(entry => String(entry?.id ?? entry?.Id ?? '') === locId)
      : null) ||
    (locName
      ? locations.find(
          entry => normalizeMatchPart(entry?.name ?? entry?.Name) === locName
        )
      : null) ||
    null;

  if (loc) {
    const hps = readHpsAlphaValues(loc);
    return hasHpsAlphaValues(hps) ? hps : null;
  }

  if (locId) return null;

  if (locations.length === 1) {
    const only = readHpsAlphaValues(locations[0]);
    return hasHpsAlphaValues(only) ? only : null;
  }

  for (const entry of locations) {
    const hps = readHpsAlphaValues(entry);
    if (hasHpsAlphaValues(hps)) return hps;
  }

  return null;
};

export const buildHpsCatalog = ({
  deliveryDetails = [],
  requirement = null,
} = {}) => {
  const entries = [];

  const pushEntry = (matchFields, source) => {
    const hps = readHpsAlphaValues(source);
    const fromLoc = readHpsFromFobLocations(source);
    const resolved = hasHpsAlphaValues(hps) ? hps : fromLoc;
    if (!resolved || !hasHpsAlphaValues(resolved)) return;
    const key = buildHpsMatchKey(matchFields);
    if (!key || key === '::::') return;
    entries.push({ key, hps: resolved });
  };

  (Array.isArray(deliveryDetails) ? deliveryDetails : []).forEach(row => {
    pushEntry(resolveOfferHpsMatchFields(row, { isCounter: false }), row);
    const locations = Array.isArray(row?.incotermFOBLocations)
      ? row.incotermFOBLocations
      : [];
    locations.forEach(loc => {
      pushEntry(
        {
          ...resolveOfferHpsMatchFields(row, { isCounter: false }),
          fobOriginLocationName: loc?.name ?? loc?.Name,
          loadingPort: loc?.name ?? loc?.Name,
          port1: loc?.name ?? loc?.Name ?? row?.port1,
          incotermLocationId: loc?.id ?? loc?.Id,
        },
        { ...row, ...loc }
      );
    });
  });

  if (requirement) {
    const reqFields = resolveOfferHpsMatchFields(
      {
        ...requirement,
        deliveryBasis:
          requirement.deliveryBasis ??
          requirement.parcelSize ??
          requirement.dischargeQty1,
        parcelSize:
          requirement.parcelSize ??
          requirement.dischargeQty1 ??
          requirement.deliveryBasis,
        parcelSize2: requirement.parcelSize2 ?? requirement.dischargeQty2,
        parcelSize3: requirement.parcelSize3 ?? requirement.dischargeQty3,
        dischargeQty1: requirement.dischargeQty1 ?? requirement.parcelSize,
        dischargeQty2: requirement.dischargeQty2 ?? requirement.parcelSize2,
        dischargeQty3: requirement.dischargeQty3 ?? requirement.parcelSize3,
      },
      { isCounter: false }
    );
    pushEntry(reqFields, requirement);
    const locations = Array.isArray(requirement.incotermFOBLocations)
      ? requirement.incotermFOBLocations
      : [];
    locations.forEach(loc => {
      pushEntry(
        {
          ...reqFields,
          port1: loc?.name ?? loc?.Name ?? reqFields.port1,
          fobOriginLocationName: loc?.name ?? loc?.Name,
          loadingPort: loc?.name ?? loc?.Name,
          incotermLocationId: loc?.id ?? loc?.Id,
        },
        { ...requirement, ...loc }
      );
    });
  }

  return entries;
};

// alphaDyedHps/alphaUndyedHps adalah nama yang benar-benar dikirim BE
// (get-delivery-detail & get-delivery-parcel, level listData maupun object lokasi).
const emptyHpsAlphaValues = () => ({
  alpha: null,
  dyed: null,
  undyed: null,
});

export const getHpsAlphaValues = (
  row,
  { isCounter = false, hpsCatalog = [] } = {}
) => {
  if (isCounter) {
    if (hpsCatalog.length) {
      const key = buildHpsMatchKey(
        resolveOfferHpsMatchFields(row, { isCounter: true })
      );
      if (key && key !== '::::') {
        const matched = hpsCatalog.find(entry => entry.key === key);
        if (matched?.hps) return matched.hps;
      }
    }
    return emptyHpsAlphaValues();
  }

  const direct = readHpsAlphaValues(row);
  if (hasHpsAlphaValues(direct)) return direct;

  const fromLocation = readHpsFromFobLocations(row);
  if (fromLocation) return fromLocation;

  if (hpsCatalog.length) {
    const key = buildHpsMatchKey(
      resolveOfferHpsMatchFields(row, { isCounter: false })
    );
    if (key && key !== '::::') {
      const matched = hpsCatalog.find(entry => entry.key === key);
      if (matched?.hps) return matched.hps;
    }
  }

  return direct;
};

export const resolveMatchingHpsKey = ({
  usesDyedUndyed,
  alphaColumns = { dyed: true, undyed: true },
  offerAlpha,
  offerDyed,
  offerUndyed,
  hps,
  statusFromApi,
  hpsAvailable = true,
}) => {
  if (!hpsAvailable) return 'unavailable';

  // Only the variants the parcel asked for are compared.
  const pairs = usesDyedUndyed
    ? [
        alphaColumns.dyed && [
          parseAlphaNumber(offerDyed),
          parseAlphaNumber(hps?.dyed),
        ],
        alphaColumns.undyed && [
          parseAlphaNumber(offerUndyed),
          parseAlphaNumber(hps?.undyed),
        ],
      ].filter(Boolean)
    : [[parseAlphaNumber(offerAlpha), parseAlphaNumber(hps?.alpha)]];

  const comparable = pairs.filter(([, hpsValue]) => hpsValue != null);
  const withOffer = comparable.filter(([offer]) => offer != null);

  if (withOffer.length) {
    const diffs = withOffer.map(([offer, hpsValue]) =>
      Math.abs(hpsValue - offer) < HPS_EQUAL_TOLERANCE ? 0 : hpsValue - offer
    );
    if (diffs.some(diff => diff < 0)) return 'higher';
    if (diffs.every(diff => diff === 0)) return 'equal';
    return 'lower';
  }

  const raw = String(
    statusFromApi ?? hps?.matchingHps ?? hps?.hpsMatching ?? ''
  )
    .trim()
    .toLowerCase();
  if (raw === 'sama' || raw === 'equal') return 'equal';
  if (raw.includes('tinggi') || raw === 'higher') return 'higher';
  if (raw.includes('rendah') || raw === 'lower') return 'lower';

  return 'unavailable';
};

export const MatchingHpsBadge = ({ matchKey }) => {
  const color =
    matchKey === 'lower'
      ? 'success'
      : matchKey === 'higher'
        ? 'danger'
        : matchKey === 'equal'
          ? 'info'
          : 'secondary';
  return (
    <Badge
      color={color}
      className='px-2 py-1 fs-11 fw-medium rounded-pill'
      style={{ whiteSpace: 'normal', lineHeight: 1.25 }}
    >
      {MATCHING_HPS_LABEL[matchKey] ?? MATCHING_HPS_LABEL.unavailable}
    </Badge>
  );
};

const splitAlphaPrice = (value, suffix = 'USD/BBL') => {
  if (value === null || value === undefined || value === '') return null;
  const formatted = formatAlphaPrice(value, suffix);
  if (!formatted || formatted === '-') return null;
  const text = String(formatted).trim();
  const match = text.match(/^(.*?)\s+(USD\/(?:BBL|MT))$/i);
  const amount = (match?.[1] || text).trim();
  const unit = match?.[2] || suffix;
  if (!amount) return null;
  return { amount, unit };
};

export const AlphaPriceStacked = ({ value, suffix }) => {
  const parsed = splitAlphaPrice(value, suffix);
  if (!parsed) return <span>-</span>;
  return (
    <span className='d-inline-flex flex-column lh-sm'>
      <span>{parsed.amount}</span>
      <span className='text-muted fw-normal fs-11'>{parsed.unit}</span>
    </span>
  );
};

export const HpsAlphaCell = ({
  usesDyedUndyed,
  alphaColumns = { dyed: true, undyed: true },
  hps,
  suffix,
  hpsAvailable,
}) => {
  if (!hpsAvailable) {
    return <span className='text-muted'>-</span>;
  }

  if (usesDyedUndyed) {
    return (
      <div className='d-flex flex-column gap-2 lh-sm fs-13'>
        {alphaColumns.dyed && (
          <div className='d-flex flex-column'>
            <span className='text-muted fs-11'>Dyed</span>
            <AlphaPriceStacked value={hps.dyed} suffix={suffix} />
          </div>
        )}
        {alphaColumns.undyed && (
          <div className='d-flex flex-column'>
            <span className='text-muted fs-11'>Undyed</span>
            <AlphaPriceStacked value={hps.undyed} suffix={suffix} />
          </div>
        )}
      </div>
    );
  }

  return <AlphaPriceStacked value={hps.alpha} suffix={suffix} />;
};
